<template>
    <div>
        <Drawer
            v-model:visible="localOpen"
            :class="[
                'note-drawer',
                drawerPosition === 'bottom' ? 'tw:h-[85vh]' : 'tw:w-full sm:tw:w-[600px]'
            ]"
            :position="drawerPosition"
            modal
            :dismissable="false"
            :close-on-escape="false"
            header="My Note"
            :pt="{
                root: drawerPosition === 'bottom' ? 'tw:h-[85vh]' : undefined
            }"
            :show-close-icon="false"
            @update:visible="handleVisibilityChange"
        >
            <template #header>
                <div class="tw:flex tw:items-center tw:justify-between tw:w-full">
                    <h2 class="title is-5">My Note</h2>

                    <div class="tw:flex tw:gap-2">
                        <auto-save :id="id" />
                        <e-button
                            :loading="isSaving"
                            @click="saveAndClose"
                        >
                            <e-icon v-if="!isSaving" icon="times" />
                            <e-loading v-else v-model:active="isSaving" />
                        </e-button>
                    </div>
                </div>
            </template>

            <div class="note-drawer-content">
                <div v-show="isLoading" class="has-text-centered p-5">
                    <e-loading :is-full-page="false" :active="isLoading" />
                    <p class="mt-3">Loading note...</p>
                </div>
                <div v-show="!isLoading">
                    <div v-if="displayCourseTitle || activityTitle">
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li v-if="displayCourseTitle" class="tw:min-w-0">
                                    <span
                                        v-tooltip:[tooltipPositionArg]="titleTooltip(displayCourseTitle)"
                                        :tabindex="titleTabindex"
                                        class="m-r-smd tw:block tw:truncate"
                                    >
                                        {{ displayCourseTitle }}
                                    </span>
                                </li>
                                <li v-if="activityTitle" class="tw:min-w-0">
                                    <span
                                        v-tooltip:[tooltipPositionArg]="titleTooltip(activityTitle)"
                                        :tabindex="titleTabindex"
                                        class="m-l-smd tw:block tw:truncate"
                                    >
                                        {{ activityTitle }}
                                    </span>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    <div>
                        <p class="is-flex">
                            <e-icon icon="user" type="is-primary" class="m-r-sm" />

                            <span class="has-text-subtle">
                                <strong>Private note:</strong> only visible to you
                            </span>
                        </p>
                    </div>

                    <div>
                        <note-form
                            v-show="!isLoading"
                            ref="noteForm"
                            :key="key"
                            :note="notes"
                            namespace="NoteDrawer"
                            edit
                            :course-id="courseId"
                            :activity-id="activityId"
                            :temp-note="tempNote"
                            @note-saved="handleNoteSaved"
                            @form-ready="handleFormReady"
                        />
                    </div>
                </div>
            </div>
        </Drawer>
    </div>
</template>

<script>
import uuidv4 from 'uuid/v4.js';
import AutoSave from '@/components/auto-save/AutoSave.vue';
import NoteForm from '@/components/notes/NoteForm.vue';

export default {
    name: 'NoteDrawer',
    components: {
        AutoSave,
        NoteForm,
    },

    emits: ['note-saved', 'drawer-closed'],

    data() {
        return {
            note: null,
            noteSingle: null,
            activeTab: 0,
            activityTitle: null,
            localOpen: false,
            isSaving: false,
        };
    },

    computed: {
        hasActiveQuiz() {
            return this.$store.getters['User/hasActiveQuiz'];
        },

        newNote() {
            return !this.id;
        },

        notes() {
            const note = this.$store.getters['NoteDrawer/note'];
            return note;
        },

        storeOpen() {
            const value = this.$store.getters['NoteDrawer/noteDrawer'];
            return value;
        },

        isLoading() {
            return this.$store.state.NoteDrawer.isLoading;
        },

        key() {
            return this.$store.getters['NoteDrawer/key'];
        },
        courseId() {
            const id = this.$route.params.courseId
                ? this.$route.params.courseId
                : this.$store.getters['NoteDrawer/courseId'];
            return id ? Number(id) : null;
        },

        courseTitle() {
            return this.$store.getters['NoteDrawer/courseTitle'];
        },

        displayCourseTitle() {
            if (this.courseTitle) {
                return this.courseTitle;
            }

            const contextId = this.courseId;

            if (!contextId) {
                return null;
            }

            if (Number(this.notes?.course_id) === contextId && this.notes?.course?.title) {
                return this.notes.course.title;
            }

            return this.course?.[contextId]?.title ?? null;
        },

        activityId() {
            if (this.newNote) {
                return this.$route.params.activityId;
            }

            return this.$store.getters['NoteDrawer/activityId'];
        },
        isNote() {
            return !this.notes;
        },
        course() {
            return this.$store.state.Courses;
        },
        // changing noteId to id
        id() {
            return this.$store.getters['NoteDrawer/noteId'];
        },

        tempNote() {
            let tempNote = this.$store.getters['Notes/all']();
            tempNote = Object.keys(tempNote).map(i => tempNote[i]);
            tempNote = tempNote.filter(x => x?.uuid != null);
            return tempNote;
        },

        drawerPosition() {
            // Use $isMobile and $isTablet (Vue global properties)
            // Breakpoint at 769px aligns with the app's tablet breakpoint
            return this.$isMobile || this.$isTablet ? 'bottom' : 'right';
        },

        tooltipPositionArg() {
            return this.isTouchDrawer ? { position: 'bottom', event: 'focus' } : { position: 'bottom' };
        },

        titleTabindex() {
            return this.isTouchDrawer ? '0' : undefined;
        },

        isTouchDrawer() {
            return this.drawerPosition === 'bottom';
        },
    },

    watch: {
        storeOpen: {
            immediate: true,
            handler(newVal) {
                this.localOpen = newVal;
                if (newVal && this.notes) {
                    this.showNoteForm();
                }
            },
        },
        'notes.id': {
            immediate: true,
            handler(newId, oldId) {
                if (this.localOpen) {
                    // Only set loading if switching to a different note (not after save/auto-save)
                    if (newId && oldId && newId !== oldId) {
                        this.$store.commit('NoteDrawer/SET_LOADING', true);
                        this.$store.commit('NoteDrawer/INCREMENT_KEY');
                        this.showNoteForm();
                    } else {
                        this.$store.commit('NoteDrawer/SET_LOADING', false);
                    }
                }
            },
        },
        '$store.state.NoteDrawer.shouldSaveBeforeClose': {
            async handler(shouldSave) {
                if (shouldSave) {
                    // Save before closing (triggered by closeNoteDrawerWithSave)
                    try {
                        await this.saveAndCloseForStore();
                    } catch (error) {
                        // Error is already logged in saveAndCloseForStore
                        // Store promise is already resolved in saveAndCloseForStore's catch block
                        // Additional error handling can be added here if needed (e.g., show user-facing error)
                    }
                }
            },
        },
    },

    methods: {
        titleTooltip(label) {
            return {
                value: label ?? '',
                class: 'note-title-tooltip',
            };
        },

        handleVisibilityChange(value) {
            if (!value) {
                this.saveAndClose();
            } else {
                this.localOpen = value;
            }
        },

        handleNoteSaved() {
            this.$emit('note-saved');
            // Close drawer after successful save
            this.isSaving = false;
            this.localOpen = false;
            this.$store.commit('NoteDrawer/CLOSE_NOTE_DRAWER');
            this.$emit('drawer-closed');
        },

        async saveAndClose() {
            // Guard against double-clicks
            if (this.isSaving) {
                return;
            }

            // If form ref is missing (e.g., clicked before ref mounts), treat like "no unsaved changes"
            // and close the drawer immediately
            if (!this.$refs.noteForm) {
                this.localOpen = false;
                this.$store.commit('NoteDrawer/CLOSE_NOTE_DRAWER');
                this.$emit('drawer-closed');
                this.$store.dispatch('NoteDrawer/notifyDrawerSaveComplete');
                return;
            }

            this.isSaving = true;
            try {
                // Call saveIfNeeded on the NoteForm component (saves only if there are unsaved changes)
                const result = await this.$refs.noteForm.saveIfNeeded();

                // Notify store that save is complete (for closeNoteDrawerWithSave coordination)
                this.$store.dispatch('NoteDrawer/notifyDrawerSaveComplete');

                // If note was empty, just close without waiting for note-saved event
                if (result && result.isEmpty) {
                    this.isSaving = false;
                    this.localOpen = false;
                    this.$store.commit('NoteDrawer/CLOSE_NOTE_DRAWER');
                    this.$emit('drawer-closed');
                } else if (!result) {
                    // No unsaved changes, so saveIfNeeded didn't call saveNote
                    // Close immediately since there's nothing to save
                    this.isSaving = false;
                    this.localOpen = false;
                    this.$store.commit('NoteDrawer/CLOSE_NOTE_DRAWER');
                    this.$emit('drawer-closed');
                }
                // Otherwise, handleNoteSaved will be called via @note-saved event
            } catch (error) {
                this.isSaving = false;

                // Still notify store that save attempt is complete (even if it failed)
                this.$store.dispatch('NoteDrawer/notifyDrawerSaveComplete');

                // Show user-facing error notification
                const errorMessage = error.message || 'An unexpected error occurred';
                this.$edverumToast.open({
                    message: `Failed to save note: ${errorMessage}. Please try again or check your connection.`,
                    type: 'is-danger',
                    duration: 5000,
                    position: 'is-top',
                });

                // Keep drawer open so user can retry or edit
            }
        },

        /**
         * Save and close triggered by store action (closeNoteDrawerWithSave).
         * This is called by the watcher when shouldSaveBeforeClose is set to true.
         */
        async saveAndCloseForStore() {
            if (!this.$refs.noteForm) {
                // No form available, just notify store and close
                this.localOpen = false;
                this.$store.commit('NoteDrawer/CLOSE_NOTE_DRAWER');
                this.$emit('drawer-closed');
                this.$store.dispatch('NoteDrawer/notifyDrawerSaveComplete');
                return;
            }

            try {
                // Call saveIfNeeded on the NoteForm component (saves only if there are unsaved changes)
                await this.$refs.noteForm.saveIfNeeded();

                // Close the drawer
                this.localOpen = false;
                this.$store.commit('NoteDrawer/CLOSE_NOTE_DRAWER');
                this.$emit('drawer-closed');

                // Notify store that save is complete (for closeNoteDrawerWithSave coordination)
                this.$store.dispatch('NoteDrawer/notifyDrawerSaveComplete');
            } catch (error) {
                // Notify store that save attempt is complete (so closeNoteDrawerWithSave promise resolves)
                // but do NOT close the drawer on error - let user see the error and retry
                this.$store.dispatch('NoteDrawer/notifyDrawerSaveComplete');

                // Rethrow error so callers can handle it if needed
                throw error;
            }
        },

        showNoteForm() {
            this.$store.commit('NoteDrawer/SHOW_NOTE_FORM');
        },

        handleFormReady() {
            this.$store.commit('NoteDrawer/SET_LOADING', false);
        },

        clear() {
            this.note = null;
            this.noteSingle = null;
            this.activity = null;
        },

        setShow() {
            if (this.notes) {
                this.show = true;
            }
        },

        async fetchActivityTitle() {
            const resp = await this.$store.dispatch('Notes/getActivity', {
                course_id: this.courseId,
                activity_id: this.activityId,
            });

            if (resp.data.data[0].title) {
                this.activityTitle = resp.data.data[0].title;
            }
        },

        async addNote() {
            this.showNoteForm();

            const note = {
                course_id: this.courseId,
                activity_id: this.activityId,
                uuid: uuidv4(),
            };

            await this.$store.commit('NoteDrawer/UPDATE_NOTE_DRAWER', {
                showNoteDrawer: true,
                isNewNote: true,
                courseTitle: null,
                note,
            });

            await this.$store.commit('NoteDrawer/UPDATE_COURSE_ID', null);
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.breadcrumb li {
    &::before {
        content: '\eb6d'; // uil-angle-right
        font-family: 'unicons';
        font-size: 1.75rem;
        line-height: 0;
    }

    &:first-child::before {
        content: '';
    }

    &:only-child {
        &::before {
            display: none;
        }
    }
}

.is-note-drawer {
    @include until($navbar-breakpoint) {
        padding-top: 100px;
    }
}
</style>

<style lang="scss">
// Override PrimeVue's default drawer bottom height
.p-drawer-bottom .note-drawer.p-drawer {
    height: 85vh !important;
}

// Desktop drawer width
.p-drawer-right .note-drawer.p-drawer {
    width: 100%;
    max-width: 600px;
}

.note-title-tooltip .p-tooltip-text {
    width: min(360px, 80vw);
    padding: 0.375rem 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
    white-space: normal;
    text-align: center;
}
</style>
